import PriceStore from "stores/PriceStore";
import { getContractByName, getTokenByAddress } from "./../eth/contracts/contracts";
import { BigNumberish, Contract, ethers } from "ethers";
import handleTransaction from "helpers/handleTransaction";
import { action, computed, observable, runInAction } from "mobx";
import { PoolData, UserPoolData } from "types";
import EthStore from "./EthStore";
import { formatResult, mapPoolInfoToClientDataObject, noExponents } from "helpers/utils";
import UserStore from "./UserStore";
import { formatUnits, parseEther } from "ethers/lib/utils";

type PoolId = string;
type UserAddress = string;

class VaultStore {
  @observable poolData: Map<PoolId, PoolData> = new Map();
  @observable userPoolData: Map<PoolId, UserPoolData> = new Map();
  @observable userBalances: Map<UserAddress, number> = new Map();
  @observable currentEpoch = 0;
  @observable epochStartBlock = 0;
  @observable YGYPerBlock = 0;
  @observable RAMPerBlock = 0;

  @observable globalAPY = 0;
  @observable userAPY = 0;

  contract: Contract | any = {};
  storage: Contract | any = {};

  @computed get pools() {
    return [...this.poolData.values()];
  }

  // For write-functions a signer is necessary while queries only require a provider.
  @action initializeContract(network: string) {
    const vault = getContractByName("Vault", network);
    const storage = getContractByName("Storage", network);
    if (vault && storage) {
      this.contract = new ethers.Contract(vault.address, vault.abi, EthStore.signer);
      this.storage = new ethers.Contract(storage.address, storage.abi, EthStore.signer);
      this.contract.on("NewEpoch", () => this.updateCallback("pool-base"));
    } else {
      console.error("Could not find find contract, wrong network supplied?");
    }
  }

  // Fetch current data for all pools
  @action async fetchAllPoolData() {
    // const poolAmount = Number(await this.contract.poolLength());
    const pools: PoolData[] = [];
    // Get the pool info, only one for now.
    const poolInfoResult: any = await this.storage.poolInfo(0);
    // Get the token used and it's balance in vault.
    const stakingToken = getTokenByAddress(poolInfoResult.token, EthStore.network);
    const poolToken = new ethers.Contract(stakingToken!.address, stakingToken!.abi, EthStore.provider);
    const totalStaked = formatResult(await poolToken.balanceOf(this.contract.address), 18);
    // Map all the data to client object.
    const clientPoolDataObject = mapPoolInfoToClientDataObject(0, poolInfoResult, totalStaked, stakingToken!.name);
    pools.push(clientPoolDataObject);
    runInAction(() => pools.forEach((poolInfo: PoolData, index: number) => this.poolData.set(index.toString(), poolInfo)));
    await this.calculateAPY(0, EthStore.account);
    await this.getCurrentEpoch();
    return;
  }

  // Fetch an users current state on a pool.
  @action async fetchUserPoolData(poolId: string) {
    const userInfo = await this.storage.userInfo(poolId, EthStore.account);
    const { pendingYGY, pendingRAM } = await this.storage.checkRewards(0, EthStore.account);
    const spentMultiplierTokens = formatResult(userInfo.spentMultiplierTokens);
    const formatted: UserPoolData = {
      amount: formatResult(userInfo.amount),
      boostAmount: formatResult(userInfo.boostAmount),
      boostLevel: Number(userInfo.boostLevel),
      rewardDebt: formatResult(userInfo.rewardDebt),
      rewardDebtYGY: formatResult(userInfo.rewardDebtYGY, 6),
      spentMultiplierTokens,
      boostCosts: await this.getBoostCosts(spentMultiplierTokens),
      pendingRAM: formatResult(pendingRAM),
      pendingYGY: parseFloat(formatUnits(pendingYGY, 6)),
    };

    runInAction(() => this.userPoolData.set(poolId.toString(), formatted));
    await this.calculateAPY(0, EthStore.account);
    return;
  }

  @action getCurrentEpoch = async () => {
    const result = Number(await this.storage.epoch());
    const block = Number(await this.storage.epochStartBlock());
    runInAction(() => (this.currentEpoch = result));
    runInAction(() => (this.epochStartBlock = block));
  };

  @action async startNewEpoch() {
    await handleTransaction(async () => this.contract.startNewEpoch(), "new epoch");
    await this.updateCallback("pool-base");
  }

  // Calculate global, pool or individuals yields
  @action async calculateAPY(poolId: number, address?: string) {
    const user = this.userPoolData.get("0");
    const poolData = this.poolData.get("0");
    const rewardsPerBlock = await this.storage.averageFeesPerBlockSinceStart();

    const averageRAMPerBlock = formatResult(rewardsPerBlock.averagePerBlock);
    const averageYGYPerBlock = parseFloat(formatUnits(rewardsPerBlock.ygyPerBlock, 18));

    let RAMPRICE = PriceStore.prices.get("RAM");
    let YGYPRICE = PriceStore.prices.get("YGY");
    let YGYRAMPRICE = PriceStore.prices.get("YGY/RAM");

    if (EthStore.network !== "homestead") {
      RAMPRICE = 24;
      YGYPRICE = 20;
      YGYRAMPRICE = 42000000;
    }

    const blockPerYear = 2407905;
    if (RAMPRICE && YGYPRICE && YGYRAMPRICE) {
      runInAction(() => {
        if (user && poolData) {
          const totalStaked = YGYRAMPRICE! * poolData.totalStaked;
          const userBoost = YGYRAMPRICE! * user.boostAmount!;
          let userRAMYield = (averageRAMPerBlock * blockPerYear * RAMPRICE!) / (totalStaked - userBoost! > 0 ? userBoost! : 0);
          let userYGYYield = (averageYGYPerBlock * blockPerYear * YGYPRICE!) / (totalStaked - userBoost! > 0 ? userBoost! : 0);
          let globalRAMYield = (averageRAMPerBlock * blockPerYear * RAMPRICE!) / totalStaked;
          let globalYGYYield = (averageYGYPerBlock * blockPerYear * YGYPRICE!) / totalStaked;
          this.RAMPerBlock = averageRAMPerBlock;
          this.YGYPerBlock = averageYGYPerBlock;
          this.globalAPY = globalRAMYield + globalYGYYield;
          const userAPY = user.amount && user.amount > 0 ? userRAMYield + userYGYYield : 0;
          this.userAPY = isFinite(userAPY) ? userAPY : this.globalAPY;
        }
      });
    }
  }

  // APY calculation is calculated as an average of fees over the last 18.66 days, current TVL and CORE price

  // Withdraws the given amount from a pool
  async withdraw(poolId: string, amount: number) {
    let parsedAmount = parseEther(noExponents(amount));
    await handleTransaction(
      async () => await this.contract.withdraw(poolId, parsedAmount),
      `withdraw of ${amount.toFixed(2)}YGY/RAM LP`,
      amount
    );
    await this.updateCallback("pool-base");
    return;
  }

  updateCallback = async (action: string) => {
    switch (action) {
      case "pool-base": {
        await Promise.all([await this.fetchAllPoolData(), await this.fetchUserPoolData("0"), await UserStore.fetchAllBalances()]);
      }
    }
  };

  // Withdraw from external account "from"
  async withdrawFrom(from: string, poolId: string, amount: number) {
    const parsedAmount = this.parseAmount(amount);
    await handleTransaction(() => this.contract.withdraw(poolId, parsedAmount), `withdraw from ${from}`, amount);
    await this.updateCallback("pool-base");
    return;
  }

  // Claim users rewards
  getRewards = async (poolId: string) => {
    await handleTransaction(async () => await this.contract.claimRewards(poolId), `claimed rewards`);
    await this.updateCallback("pool-base");
    return;
  };

  // Deposit tokens for a pool
  async deposit(poolId: string, amount: number) {
    let parsedAmount = parseEther(noExponents(amount));
    await handleTransaction(
      async () => await this.contract.deposit(poolId, parsedAmount),
      `deposit of ${amount.toFixed(10)}YGY/RAM LP`,
      amount
    );
    await this.updateCallback("pool-base");
    return;
  }

  // Deposit for another user holding "toAddress"
  async depositFor(toAddress: string, poolId: string, amount: number) {
    const parsedAmount = this.parseAmount(amount);
    await handleTransaction(() => this.contract.depositFor(toAddress, poolId, parsedAmount), `deposit for ${toAddress}`, amount);
    await this.updateCallback("pool-base");
    return;
  }

  // Get token usage allowancee for the pool
  async getAllowanceForPool(spender: string, poolId: string, amount: number = 99999999999) {
    const parsedAmount = this.parseAmount(amount);
    await handleTransaction(
      async () => await this.contract.setAllowanceForPoolToken(spender, poolId, parsedAmount),
      `allowance set for ${spender} in ${poolId}`,
      amount
    );
    await this.updateCallback("allowance");
    return;
  }

  // Gets the cost to purchase a level
  async getBoostCost(level: number) {
    return this.formatAmount(await this.contract.calculateCost(level));
  }

  getBoostCosts = async (spent: number) => {
    const levels = [1, 2, 3, 4];
    const promises = levels.map((level) => this.storage.boostLevelCosts(level));
    const costs = await Promise.all(promises);
    return costs.map((levelCost) => formatResult(levelCost) - spent);
  };

  // Purchase a multiplier level for a pool.
  purchase = async (poolId: string, level: number) => {
    await handleTransaction(async () => await this.contract.purchase(poolId, level), `boosting to ${level}`, level);
    await this.updateCallback("pool-base");
    return;
  };

  // Get users total multiplier
  async getTotalMultiplier(address: string) {
    const result = await this.contract.getTotalMultiplier(address);
    return result;
  }

  distributeFees = async () => {
    await handleTransaction(() => this.contract.distributeFees(), `fee distribution`);
    await this.updateCallback("pool-base");
  };

  transferDevFee = async () => {
    await handleTransaction(() => this.contract.transferDevFee(), `dev distribution`);
    await this.updateCallback("pool-base");
  };

  // Transform regular inputs to wei amounts
  parseAmount(amount: number, decimals = 18) {
    return ethers.utils.parseUnits(amount.toString(), decimals);
  }

  // Transform wei amounts to regular numbers
  formatAmount(amount: BigNumberish, decimals = 18) {
    return ethers.utils.formatUnits(amount, decimals);
  }
}

export default new VaultStore();
